import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { featuredCategoryTags } from "@/mocks/categories";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const goSearch = (value: string) => {
    const q = value.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setKeyword("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-foreground-950/50 px-4 pt-24 animate-fade-in" role="dialog" aria-modal="true">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true"></div>
      <div className="relative w-full max-w-2xl rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="flex items-center gap-3">
          <i className="ri-search-line text-xl text-foreground-400" aria-hidden="true"></i>
          <input
            ref={inputRef}
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") goSearch(keyword);
            }}
            placeholder="搜索电影名称、类型、导演或主演…"
            className="w-full border-0 bg-transparent text-base text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800 cursor-pointer"
            aria-label="关闭搜索"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        <div className="mt-5 border-t border-background-200 pt-4">
          <p className="font-label text-xs text-foreground-500">热门搜索</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {["出轨的妻子", "悬疑", "爱情", "国产剧情", "高分推荐"].map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => goSearch(tag)}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 font-label text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
              >
                {tag}
              </button>
            ))}
            {featuredCategoryTags.slice(0, 2).map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => goSearch(tag)}
                className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1.5 font-label text-xs text-accent-900 transition-colors hover:bg-accent-200 cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => goSearch(keyword)}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-search-line" aria-hidden="true"></i>
            开始搜索
          </button>
        </div>
      </div>
    </div>
  );
}